import React, { useCallback, useRef, useState } from "react";
import { FileUploadAltIcon } from "./file-upload-alt-icon";
import "./upload-dropzone.scss";

export interface UploadDropzoneProps {
  /**
   * Called with the files picked by clicking or dropped onto the zone that
   * pass `accept` and `maxSizeBytes`. Rejected files go to `onFileRejected`.
   */
  onFilesSelected?: (files: File[]) => void;
  /**
   * Called when a dropped or picked file is rejected by `accept` or
   * `maxSizeBytes`.
   */
  onFileRejected?: (file: File, reason: "type" | "size") => void;
  /**
   * Comma-separated list of accepted types, e.g. `".pdf"` or
   * `".pdf,.jpg,.png"`. When set, non-matching files are rejected.
   */
  accept?: string;
  /**
   * Maximum size in bytes. Files above this are rejected.
   */
  maxSizeBytes?: number;
  /** Primary prompt line. */
  label?: string;
  /**
   * A phrase within `label` rendered in the accent color and bold, matching
   * the Figma design where "click here" stands out from the rest of the line.
   * When omitted, the whole line uses the accent color.
   */
  highlight?: string;
  /** Secondary hint line. */
  hint?: string;
  /** Disables interaction and dims the zone. */
  disabled?: boolean;
  /** Accessible label for the dropzone. */
  "aria-label"?: string;
  className?: string;
}

/**
 * Compact upload dropzone from the Medical PriorAuth Figma design
 * (node 22049-222675): a file icon above a two-line prompt, accepting
 * clicks and drag-and-drop. The first line highlights `highlight` in the
 * accent color; the rest of the line is dark.
 */
export const UploadDropzone: React.FC<UploadDropzoneProps> = (props) => {
  const {
    onFilesSelected,
    onFileRejected,
    accept,
    maxSizeBytes,
    label = "Drag and drop or click here to upload",
    highlight = "click here",
    hint = "Supported file format .pdf, not exceeding 40MB",
    disabled = false,
    className = "",
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepth = useRef(0);

  const acceptedTypes = React.useMemo(() => {
    if (!accept) return null;
    return accept
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
  }, [accept]);

  const validate = useCallback(
    (file: File): "type" | "size" | null => {
      if (acceptedTypes) {
        const name = file.name.toLowerCase();
        const type = file.type.toLowerCase();
        const matches = acceptedTypes.some((t) => {
          if (t.startsWith(".")) return name.endsWith(t);
          if (t.endsWith("/*")) return type.startsWith(t.slice(0, -1));
          return type === t;
        });
        if (!matches) return "type";
      }
      if (maxSizeBytes != null && file.size > maxSizeBytes) return "size";
      return null;
  }, [acceptedTypes, maxSizeBytes]);

  const handleFiles = useCallback(
    (list: FileList | null) => {
      if (!list) return;
      const files = Array.from(list);
      const accepted: File[] = [];
      for (const file of files) {
        const reason = validate(file);
        if (reason) {
          onFileRejected?.(file, reason);
        } else {
          accepted.push(file);
        }
      }
      if (accepted.length) onFilesSelected?.(accepted);
    },
    [onFilesSelected, onFileRejected, validate]
  );

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPicker();
    }
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    if (disabled) return;
    dragDepth.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragOver(false);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (disabled) return;
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    dragDepth.current = 0;
    setIsDragOver(false);
    if (disabled) return;
    handleFiles(event.dataTransfer.files);
  };

  // Split the label around the first occurrence of `highlight` so the phrase
  // can be rendered in the accent color while the rest stays dark.
  const labelParts = React.useMemo(() => {
    if (!highlight) return { before: "", middle: label, after: "" };
    const index = label.indexOf(highlight);
    if (index === -1) return { before: "", middle: label, after: "" };
    return {
      before: label.slice(0, index),
      middle: highlight,
      after: label.slice(index + highlight.length),
    };
  }, [label, highlight]);

  const stateClass = [
    "upload-dropzone",
    disabled ? "upload-dropzone--disabled" : "",
    isDragOver ? "upload-dropzone--drag-over" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={stateClass}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-label={props["aria-label"] ?? label}
      onClick={openPicker}
      onKeyDown={handleKeyDown}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <FileUploadAltIcon />
      <div className="upload-dropzone__text">
        <span className="upload-dropzone__label">
          {labelParts.before && (
            <span className="upload-dropzone__label-muted">
              {labelParts.before}
            </span>
          )}
          <span className="upload-dropzone__label-accent">
            {labelParts.middle}
          </span>
          {labelParts.after && (
            <span className="upload-dropzone__label-muted">
              {labelParts.after}
            </span>
          )}
        </span>
        <span className="upload-dropzone__hint">{hint}</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        className="upload-dropzone__input"
        accept={accept}
        multiple
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          handleFiles(event.target.files);
          // Allow the same file to be picked again on a later click.
          event.target.value = "";
        }}
      />
    </div>
  );
};